import React, { useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    Avatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress,
    Chip,
    Fade,
    Switch,
    FormControlLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import {
    useGetSingleEmployee,
    useUpdateDeduction,
    useUpdateWorkdays,
    useSalaryPreview,
} from "../../hooks/useEmployees";
import { useAlert } from "../../context/AlertContext";
import { isFoodAllowanceApplicable } from "../../utils/foodAllowance";
import { firstSet, historyTotalForMonth } from "../../utils/payrollValues";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
} from "recharts";

const monthName = (d) =>
    new Intl.DateTimeFormat("en-US", { month: "long" }).format(d);

const monthOrder = {
    "January": 1, "February": 2, "March": 3, "April": 4, "May": 5, "June": 6,
    "July": 7, "August": 8, "September": 9, "October": 10, "November": 11, "December": 12
};

const sortChronologically = (a, b) => {
    if (Number(a.year) !== Number(b.year)) return Number(a.year) - Number(b.year);
    return monthOrder[a.month] - monthOrder[b.month];
};

const EmployeePayrollDetails = ({ selectedEmp, month, year, onBack }) => {
    const { data, isLoading } = useGetSingleEmployee({
        id: selectedEmp?.employee_no,
    });
    const { mutate: updateDeductionMutation } = useUpdateDeduction();
    const { mutate: updateWorkdaysMutation } = useUpdateWorkdays();
    const { showError, showSuccess } = useAlert();

    const [currentDate] = useState(new Date());
    const [editingCell, setEditingCell] = useState(null); // format: { field: "loan", type: "deduction" | "workday", value: "" }

    // The month picked on the Salary View list; today's month only when none was passed.
    const currentMonthStr = month || monthName(currentDate);
    const currentYear = Number(year) || currentDate.getFullYear();

    const { data: preview } = useSalaryPreview({
        employeeNo: selectedEmp?.employee_no,
        month: currentMonthStr,
        year: currentYear,
    });
    const engine = preview?.details;

    // Get latest available payslip for summary metrics as fallback
    const sortedPayslips = [...(data?.payslips || [])].sort(sortChronologically);
    const latestPayslip = sortedPayslips[sortedPayslips.length - 1] || {};

    // Earnings must come from a record that actually has them. An inline override
    // edit (update_loan) creates a current-month GrossPay row with basic=0; that
    // zero placeholder would otherwise SHADOW the processed payslip and make every
    // earning render ₹0. So a zero-earnings current-month GrossPay falls through to
    // the current-month payslip (the real processed salary), then the latest payslip.
    const hasEarnings = (r) => r && Number(r.basic) > 0;
    const currentMonthGross = data?.gross_pay?.find(
        (g) => g.month === currentMonthStr && g.year === currentYear
    );
    const currentMonthPayslip = sortedPayslips.find(
        (p) => p.month === currentMonthStr && p.year === currentYear
    );
    const currentGrossPay =
        (hasEarnings(currentMonthGross) ? currentMonthGross : null) ||
        currentMonthPayslip ||
        (hasEarnings(latestPayslip) ? latestPayslip : null) ||
        currentMonthGross ||
        data?.gross_pay?.slice(-1)[0] ||
        {};

    const currentDeductions = data?.total_deductions?.find(
        (d) => d.month === currentMonthStr && d.year === currentYear
    ) || latestPayslip || data?.total_deductions?.slice(-1)[0] || {};

    const currentNetPay = data?.net_pay?.find(
        (n) => n.month === currentMonthStr && n.year === currentYear
    ) || latestPayslip || data?.net_pay?.slice(-1)[0] || {};

    const currentWorkdays = data?.workdays?.find(
        (w) => w.month === currentMonthStr && w.year === currentYear
    ) || latestPayslip || data?.workdays?.slice(-1)[0] || {};

    // Construct chart data dynamically from processed payslips history (tracking paid months only)
    const chartData = sortedPayslips
        .slice(-6)
        .map((item) => ({
            name: `${item.month ? item.month.substring(0, 3) : ''} ${item.year || ''}`,
            netPay: Number(item.net_pay) || 0,
        }));

    // SalaryMaster holds sticky per-employee overrides (PF, Insurance, ESI, Income Tax, Travel Allowance, etc.)
    const salaryMaster = Array.isArray(data?.salary_master) ? data.salary_master[0] : data?.salary_master;

    // A manual earning override is saved to the current-month GrossPay with basic=0,
    // which `currentGrossPay` intentionally skips in favour of the processed payslip (so
    // Basic/HRA don't render ₹0). That would hide a just-saved override, so for the
    // EDITABLE fields prefer the raw current-month GrossPay value whenever it is SET.
    // "Set" includes 0: HR clearing a component to zero for the month is a real value,
    // and `> 0` used to drop it and show the sticky SalaryMaster amount instead (#48343).
    // firstSet now comes from utils/payrollValues so the tests cover the shipped copy.
    const monthOverride = (field) => firstSet(currentMonthGross?.[field]);

    // Incentives are period-specific. Never take position [0] from the employee's
    // complete history: that can display an old month's incentive in every period.
    const currentPeriodIncentive = historyTotalForMonth(
        data?.incentive_history_overall,
        'incentive_amount',
        currentMonthStr,
        currentYear
    );

    // A positive GrossPay food value is a manual override for this month. Zero means
    // "automatic", so show the attendance-derived preview amount that Processing will
    // use instead of misleading HR with a stored zero.
    const foodOverrideSet = Boolean(currentMonthGross?.food_allowance_override_set);
    const displayedFoodAllowance = foodOverrideSet
        ? currentMonthGross?.food_allowance
        : firstSet(engine?.food_allowance, currentMonthPayslip?.food_allowance, 0);

    // Food allowance is HO-file only: non-HO employees do not receive it,
    // so the row is hidden entirely for them (CRM #44163).
    // Merge rather than `||`: selectedEmp is the list row (may lack team_head) and
    // data.employee is the loaded record (absent until the fetch resolves). Taking
    // one whole object meant a missing team_head decided the answer on its own.
    const foodApplies = isFoodAllowanceApplicable({ ...(selectedEmp || {}), ...(data?.employee || {}) });

    // Define Earnings & Deductions arrays for tables
    const earningsItems = [
        { label: "Basic", field: "basic", value: currentGrossPay?.basic, editable: false },
        { label: "HRA", field: "hra", value: currentGrossPay?.hra, editable: false },
        { label: "Conveyance", field: "conveyance", value: currentGrossPay?.conveyance, editable: false },
        { label: "Medical", field: "medical", value: currentGrossPay?.medical, editable: false },
        { label: "Special Allowance", field: "special_allowance", value: currentGrossPay?.special_allowance, editable: false },
        { label: "Telephone Allowance", field: "telephone_allowance", value: firstSet(monthOverride('telephone_allowance'), currentGrossPay?.telephone_allowance, salaryMaster?.telephone_allowance), editable: true, type: "deduction" },
        { label: "Recharge", field: "recharge", value: firstSet(monthOverride('recharge'), currentGrossPay?.recharge, salaryMaster?.recharge), editable: true, type: "deduction" },
        // Auto-fills from attendance (qualifying punch-in days × ₹70). Editable: a manual
        // value overrides for THAT month (prorated like the others); clearing it back to
        // 0 reverts to the automatic attendance amount. Hidden completely for non-HO employees.
        ...(foodApplies ? [{
            label: "Food Allowance",
            field: "food_allowance",
            value: displayedFoodAllowance,
            editable: true,
            type: "deduction"
        }] : []),
        { label: "Travel Allowance", field: "travel_allowance", value: firstSet(monthOverride('travel_allowance'), currentGrossPay?.travel_allowance, salaryMaster?.travel_allowance), editable: true, type: "deduction" },
        { label: "Exam Increment", field: "exam_increment", value: firstSet(monthOverride('exam_increment'), currentGrossPay?.exam_increment, salaryMaster?.exam_increment), editable: true, type: "deduction" },
        { label: "Incentive", field: "incentive", value: firstSet(currentPeriodIncentive, engine?.incentive, currentMonthPayslip?.incentive, salaryMaster?.incentive, 0), editable: true, type: "deduction" },
        { label: "Bonus", field: "bonus", value: firstSet(monthOverride('bonus'), currentGrossPay?.bonus, salaryMaster?.bonus), editable: true, type: "deduction" },
        { label: "Other Earnings", field: "other_earnings", value: firstSet(monthOverride('other_earnings'), currentGrossPay?.other_earnings, salaryMaster?.other_earnings), editable: true, type: "deduction" },
    ];


    // PF override: sticky per-employee from SalaryMaster; falls back to current-month snapshot, then CTC-derived employer_pf
    const employeePfOverride = salaryMaster?.employee_pf_override;
    const employerPfOverride = salaryMaster?.employer_pf_override;
    const pfEmployeeValue = employeePfOverride ?? currentDeductions?.pf ?? latestPayslip?.pf ?? 0;
    const pfEmployerValue = employerPfOverride ?? currentDeductions?.employer_pf_deduction ?? currentGrossPay?.employer_pf ?? latestPayslip?.employer_pf ?? 0;
    // The preview is the effective company + employee PF decision used by the
    // calculation engine. Prefer it so the switch cannot disagree with the PF
    // amounts after a bulk update while an older employee-detail query is cached.
    const employeePfEnabled = Boolean(firstSet(
        engine?.pf_enabled,
        salaryMaster?.pf_enabled,
        Number(salaryMaster?.full_employer_pf) > 0
    ));

    // Sticky precedence: month snapshot → SalaryMaster override → latest historical → hardcoded default (500)
    // 500 is the default only when nothing is set anywhere - a stored 0 (insurance not
    // deducted this month) must print as 0, not silently become 500 (#48343).
    const insuranceValue = firstSet(currentDeductions?.prof_tax, salaryMaster?.prof_tax_override, latestPayslip?.professional_tax, 500);
    const esiValue = currentDeductions?.esi ?? salaryMaster?.esi_override ?? latestPayslip?.esi ?? 0;
    const incomeTaxValue = currentDeductions?.income_tax ?? salaryMaster?.income_tax_override ?? latestPayslip?.income_tax ?? 0;

    // Mirrors backend get_deduction(): a non-zero month value, else the sticky SalaryMaster
    // value, else 0. Showing only the month row hid sticky amounts that payroll still deducts.
    const currentMonthDeduction = data?.total_deductions?.find(
        (d) => d.month === currentMonthStr && d.year === currentYear
    );
    const engineDeduction = (field) =>
        Number(currentMonthDeduction?.[field]) || Number(salaryMaster?.[field]) || 0;

    // Deductions come from the same calculation Process uses for the salary file
    // (salary-preview endpoint). The local values are only a fallback while it loads or fails.
    const fromEngine = (key, fallback) => (engine ? engine[key] : fallback);

    const deductionItems = [
        { label: "PF (Employee)", field: "pf", value: fromEngine("pf", pfEmployeeValue), editable: true, type: "deduction" },
        { label: "PF (Employer)", field: "employer_pf", value: fromEngine("employer_pf_deduction", pfEmployerValue), editable: true, type: "deduction" },
        { label: "ESI", field: "esi", value: fromEngine("esi", esiValue), editable: true, type: "deduction" },
        { label: "Insurance", field: "prof_tax", value: fromEngine("professional_tax", insuranceValue), editable: true, type: "deduction" },
        { label: "Income Tax", field: "income_tax", value: fromEngine("income_tax", incomeTaxValue), editable: true, type: "deduction" },
        { label: "Penalty", field: "penalty", value: fromEngine("penalty", engineDeduction("penalty")), editable: true, type: "deduction" },
        { label: "Loan", field: "loan", value: fromEngine("loan", engineDeduction("loan")), editable: true, type: "deduction" },
        { label: "Other Deduction", field: "other_deduction", value: fromEngine("other_deduction", engineDeduction("other_deduction")), editable: true, type: "deduction" },
        { label: "LOP", field: "lop", value: fromEngine("lop_days", currentWorkdays?.lop), editable: true, type: "workday" },
    ];


    const handleEditClick = (item) => {
        setEditingCell({
            field: item.field,
            type: item.type,
            value: item.value || "0.00",
        });
    };

    const handleEditCancel = () => setEditingCell(null);

    const handleEmployeePfToggle = (event) => {
        updateDeductionMutation(
            {
                employee_no: selectedEmp?.employee_no,
                month: currentMonthStr,
                year: currentYear,
                pf_enabled: event.target.checked,
            },
            {
                onSuccess: () => showSuccess(`Employee PF turned ${event.target.checked ? 'ON' : 'OFF'}.`),
                onError: (err) => showError(err?.response?.data?.error || "Error updating employee PF setting."),
            }
        );
    };


    const handleEditSave = async () => {
        try {
            if (editingCell.type === "workday") {
                updateWorkdaysMutation(
                    {
                        employee_no: selectedEmp?.employee_no,
                        month: currentMonthStr,
                        year: currentYear,
                        [editingCell.field]: Number(editingCell.value),
                    },
                    {
                        onSuccess: () => {
                            showSuccess("Workdays updated successfully.");
                            setEditingCell(null);
                        },
                        onError: (err) => {
                            showError(err?.response?.data?.error || "Error updating data.");
                        },
                    }
                );
            } else {
                updateDeductionMutation(
                    {
                        employee_no: selectedEmp?.employee_no,
                        month: currentMonthStr,
                        year: currentYear,
                        [editingCell.field]: parseFloat(editingCell.value),
                    },
                    {
                        onSuccess: () => {
                            showSuccess("Component updated successfully.");
                            setEditingCell(null);
                        },
                        onError: (err) => {
                            showError(err?.response?.data?.error || "Error updating data.");
                        },
                    }
                );
            }
        } catch (err) {
            console.error(err);
            showError("Error processing update.");
        }
    };

    const calculateTotal = (arrayItems) => {
        return arrayItems
            .filter((i) => i.value !== undefined && i.value !== null)
            .reduce((acc, curr) => acc + parseFloat(curr.value || 0), 0)
            .toFixed(2);
    };

    // Real CTC lives in CTCItems (set by the salary calc/import), so it matches the
    // earnings breakdown below. Prefer it; fall back to gross pay, and only then to
    // ctc_revision — that revision can be an OLD/stale figure (the backend returns the
    // earliest one), which is what used to make this header disagree with the salary.
    // NOTE: mirrors the (correct) preference order already used by the payroll list view.
    const monthlyCTC = data?.ctc_items?.[0]?.monthly_ctc || currentGrossPay?.gross_pay || data?.ctc_revision?.amount || "0.00";
    const annualCTC = parseFloat(monthlyCTC * 12).toLocaleString("en-IN", { minimumFractionDigits: 2 });

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
                <CircularProgress sx={{ color: "#6366f1" }} />
            </Box>
        );
    }

    return (
        <Fade in timeout={500}>
            <Box sx={{ minHeight: "100vh", bgcolor: "#fafafa", p: { xs: 2, md: 4 } }}>
                {/* Navigation & Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, flexWrap: "wrap", gap: 2 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={onBack}
                        sx={{
                            color: "#4b5563",
                            bgcolor: "#fff",
                            borderRadius: 2,
                            px: { xs: 2, md: 3 },
                            py: 1,
                            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                            "&:hover": { bgcolor: "#f3f4f6" },
                            textTransform: "none",
                            fontWeight: 600,
                        }}
                    >
                        Back to Payroll Overview
                    </Button>

                    <Paper sx={{ display: "flex", alignItems: "center", gap: 2, p: "12px 24px", borderRadius: 4, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", bgcolor: "#fff" }}>
                        <Avatar sx={{ width: 48, height: 48, bgcolor: "#6366f1", fontWeight: 700, fontSize: "1.2rem" }}>
                            {selectedEmp?.name?.charAt(0)}
                        </Avatar>
                        <Box>
                            <Typography variant="h6" fontWeight={800} color="#111827" sx={{ lineHeight: 1.2 }}>
                                {selectedEmp?.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                Emp ID: {selectedEmp?.employee_no} | {selectedEmp?.department || "General"}
                            </Typography>
                        </Box>
                    </Paper>
                </Box>

                {/* Dynamic Analytics Dashboard */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {/* Net Pay Trends Chart */}
                    <Grid item xs={12} md={8}>
                        <Paper sx={{ p: 3, borderRadius: 4, boxShadow: "0px 4px 20px rgba(0,0,0,0.03)", height: "100%" }}>
                            <Typography variant="h6" fontWeight={700} color="#111827" mb={3}>
                                Net Pay History Tracker
                            </Typography>
                            <Box sx={{ height: 280, width: "100%" }}>
                                {chartData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={chartData}>
                                            <defs>
                                                <linearGradient id="colorNetPay" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                                                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} dy={10} />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} dx={-10} tickFormatter={(val) => `${val / 1000}k`} />
                                            <RechartsTooltip cursor={{ fill: 'rgba(99, 102, 241, 0.05)' }} contentStyle={{ borderRadius: 8, border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.1)" }} />
                                            <Area type="monotone" dataKey="netPay" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorNetPay)" activeDot={{ r: 6, fill: "#4f46e5", stroke: "#fff", strokeWidth: 2 }} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                                        <Typography color="text.secondary">No historical data found</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Key Metrics summary */}
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 4, borderRadius: 4, boxShadow: "0px 4px 20px rgba(0,0,0,0.03)", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", bgcolor: "#111827", color: "#fff" }}>
                            <Typography variant="subtitle2" sx={{ color: "#9ca3af", letterSpacing: 1, textTransform: "uppercase", mb: 1 }}>Current Period Net Pay</Typography>
                            <Typography variant="h3" fontWeight={800} sx={{ mb: 2, background: "linear-gradient(to right, #6366f1, #a855f7)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                                ₹ {currentNetPay?.net_pay || "0.00"}
                            </Typography>
                            <Chip label={`${currentMonthStr} ${currentYear}`} sx={{ alignSelf: "flex-start", bgcolor: "rgba(255,255,255,0.1)", color: "#fff", fontWeight: 600, mb: 4 }} />

                            <Box sx={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid rgba(255,255,255,0.1)", pt: 2 }}>
                                <Box>
                                    <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", mb: 0.5 }}>Monthly CTC</Typography>
                                    <Typography variant="body1" fontWeight={700} sx={{ color: "#fff" }}>
                                        ₹ {parseFloat(monthlyCTC).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", mb: 0.5 }}>Annual CTC</Typography>
                                    <Typography variant="body1" fontWeight={700} sx={{ color: "#fff" }}>
                                        ₹ {annualCTC}
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: "right" }}>
                                    <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", mb: 0.5 }}>Total Deductions</Typography>
                                    <Typography variant="body1" fontWeight={700} color="#f87171">
                                        ₹ {calculateTotal(deductionItems)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Detailed Breakdown Tables */}
                <Grid container spacing={4}>
                    {/* EARNINGS TABLE */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ borderRadius: 4, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.03)" }}>
                            <Box sx={{ p: 3, borderBottom: "1px solid #f3f4f6", bgcolor: "#f9fafb" }}>
                                <Typography variant="h6" fontWeight={700} color="#111827">Earnings Calculation</Typography>
                            </Box>
                            <TableContainer>
                                <Table size="medium">
                                    <TableBody>
                                        {earningsItems.map((item, idx) => {
                                            const isEditing = editingCell?.field === item.field && editingCell?.type === item.type;
                                            return (
                                                <TableRow key={idx} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                                    <TableCell sx={{ color: "#4b5563", fontWeight: 500, width: "60%" }}>{item.label}</TableCell>
                                                    <TableCell align="right" sx={{ position: "relative" }}>
                                                        {isEditing ? (
                                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                                                                <TextField
                                                                    size="small"
                                                                    variant="outlined"
                                                                    autoFocus
                                                                    type="number"
                                                                    value={editingCell.value}
                                                                    onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter") handleEditSave();
                                                                        if (e.key === "Escape") handleEditCancel();
                                                                    }}
                                                                    InputProps={{ sx: { height: 32, fontSize: "0.875rem", width: 100 } }}
                                                                />
                                                                <IconButton size="small" onClick={handleEditSave} sx={{ color: "#10b981" }}><SaveIcon fontSize="small" /></IconButton>
                                                                <IconButton size="small" onClick={handleEditCancel} sx={{ color: "#ef4444" }}><CloseIcon fontSize="small" /></IconButton>
                                                            </Box>
                                                        ) : (
                                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                                                                <Typography fontWeight={item.editable ? 700 : 500} color={item.editable ? "#111827" : "inherit"}>
                                                                    ₹ {parseFloat(item.value || 0).toFixed(2)}
                                                                </Typography>
                                                                {item.editable && (
                                                                    <Tooltip title="Edit Value">
                                                                        <IconButton size="small" onClick={() => handleEditClick(item)} sx={{ color: "#6366f1", p: 0.5 }}>
                                                                            <EditIcon fontSize="small" sx={{ width: 16, height: 16 }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    {/* DEDUCTIONS TABLE */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ borderRadius: 4, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.03)" }}>
                            <Box sx={{ p: 3, borderBottom: "1px solid #f3f4f6", bgcolor: "#fff5f5", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="h6" fontWeight={700} color="#991b1b">Deductions Calculation</Typography>
                                <FormControlLabel
                                    control={<Switch checked={employeePfEnabled} onChange={handleEmployeePfToggle} />}
                                    label={`Employee PF ${employeePfEnabled ? 'ON' : 'OFF'}`}
                                />
                            </Box>
                            <TableContainer>
                                <Table size="medium">
                                    <TableBody>
                                        {deductionItems.map((item, idx) => {
                                            const isEditing = editingCell?.field === item.field && editingCell?.type === item.type;
                                            return (
                                                <TableRow key={idx} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                                    <TableCell sx={{ color: "#4b5563", fontWeight: 500, width: "60%" }}>{item.label}</TableCell>
                                                    <TableCell align="right">
                                                        {isEditing ? (
                                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                                                                <TextField
                                                                    size="small"
                                                                    variant="outlined"
                                                                    autoFocus
                                                                    type="number"
                                                                    value={editingCell.value}
                                                                    onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter") handleEditSave();
                                                                        if (e.key === "Escape") handleEditCancel();
                                                                    }}
                                                                    InputProps={{ sx: { height: 32, fontSize: "0.875rem", width: 100 } }}
                                                                />
                                                                <IconButton size="small" onClick={handleEditSave} sx={{ color: "#10b981" }}><SaveIcon fontSize="small" /></IconButton>
                                                                <IconButton size="small" onClick={handleEditCancel} sx={{ color: "#ef4444" }}><CloseIcon fontSize="small" /></IconButton>
                                                            </Box>
                                                        ) : (
                                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                                                                <Typography fontWeight={item.editable ? 700 : 500} color={item.editable ? "#991b1b" : "inherit"}>
                                                                    ₹ {parseFloat(item.value || 0).toFixed(2)}
                                                                </Typography>
                                                                {item.editable && (
                                                                    <Tooltip title="Edit Component">
                                                                        <IconButton size="small" onClick={() => handleEditClick(item)} sx={{ color: "#6366f1", p: 0.5 }}>
                                                                            <EditIcon fontSize="small" sx={{ width: 16, height: 16 }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </Fade>
    );
};

export default EmployeePayrollDetails;
